import { useEffect, useState } from 'react';

const listeners = new Set<(value: boolean) => void>();
let isModifierPressed = false;
let isListening = false;

const canUseDom = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const notifyListeners = () => {
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  listeners.forEach((listener) => listener(isModifierPressed));
};

const setModifierPressed = (nextValue: boolean) => {
  if (nextValue === isModifierPressed) return;
  isModifierPressed = nextValue;
  notifyListeners();
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.metaKey || event.ctrlKey) {
    setModifierPressed(true);
  }
};

const handleKeyUp = (event: KeyboardEvent) => {
  if (event.key === 'Meta' || event.key === 'Control') {
    setModifierPressed(event.metaKey || event.ctrlKey);
  }
};

const handleBlur = () => {
  setModifierPressed(false);
};

const startListening = () => {
  if (isListening || !canUseDom()) return;
  isListening = true;
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('blur', handleBlur);
};

const stopListening = () => {
  if (!isListening || !canUseDom()) return;
  isListening = false;
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener('blur', handleBlur);
  setModifierPressed(false);
};

const subscribe = (listener: (value: boolean) => void) => {
  listeners.add(listener);
  startListening();
  listener(isModifierPressed);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopListening();
    }
  };
};

/**
 * Hook to track when a modifier key (Cmd on Mac, Ctrl on Windows/Linux) is pressed.
 * Returns true when the modifier key is currently held down.
 *
 * Pass `enabled: false` to opt out entirely: no listener is registered and the
 * hook stays `false`, so a caller that only cares while a feature is on does
 * not re-render on every ⌘ press (⌘C, ⌘R, ⌘Tab…) the rest of the time.
 */
export const useModifierKeyPressed = (enabled = true) => {
  const [isPressed, setIsPressed] = useState(enabled && isModifierPressed);

  useEffect(() => {
    if (!enabled) {
      setIsPressed(false);
      return;
    }
    return subscribe(setIsPressed);
  }, [enabled]);

  return enabled && isPressed;
};
